import usePrefix from '@/hooks/usePrefix';
import { modalType } from '@components/Modal';
import { emailIsValid } from '@helpers/email';
import AuthStore from '@stores/AuthStore';
import { useMobxStores } from '@stores/index';
import { Button, Form, Input } from 'antd';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import Head from 'next/head';
import { useState } from 'react';
import { GetServerSideProps } from 'next';
import { basicPageProps } from '@/utils/isServer';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
    },
  };
};

export const doForgot = async (userEmail: string, authStore: AuthStore) => {
  const memberId = await authStore.doForgot(userEmail ?? '');
  switch (memberId) {
    case -1:
      modalType({
        type: 'error',
        title: 'Error',
        content:
          'Could not contact server - please check your internet connection.',
      });
      break;
    case 0:
      modalType({
        type: 'error',
        title: 'Error',
        content:
          "Couldn't find account - please verify the email is correct and try again.",
      });
      break;
    default:
      modalType({
        type: 'success',
        title: 'Password sent!',
        content:
          'Thanks! We’ve sent you an email with the information needed to reset your password!',
      });
  }
};

function Forgot() {
  const prefix = usePrefix();
  const [form] = Form.useForm();
  const [email, setEmail] = useState('');
  const [validEmailFlag, setValidEmailFlag] = useState(false);

  const { authStore } = useMobxStores();

  function handleOnFinish(values) {
    const { email } = values;
    doForgot(email, authStore);
  }

  return (
    <>
      <Head>
        <title>Forgot Password? | On Air Parking</title>
        <meta name="description" content="Reset your password" />
      </Head>
      <div className="container mx-auto px-4">
        <h2 className="text-3xl mx-auto text-center py-8">Forgot Password?</h2>
        <Form
          className="max-w-md border mx-auto bg-gray-100 p-8"
          onFinish={handleOnFinish}
          layout="vertical"
          form={form}
        >
          <div className="flex flex-col">
            <Form.Item
              name="email"
              rules={[
                {
                  type: 'email',
                  required: true,
                  message: 'Not a valid email!',
                },
              ]}
              htmlFor="auth_email"
              label="E-mail"
            >
              <Input
                type="email"
                inputMode="email"
                id="auth_email"
                onChange={(e) => {
                  setValidEmailFlag(emailIsValid(e.target.value));
                }}
                className="rounded-full py-2 px-4"
              />
            </Form.Item>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            className={`bg-primary rounded-full mx-auto table px-8 text-white text-sm font-bold uppercase mb-8`}
            size="large"
            loading={authStore.forgottenPasswordEmailRequested}
          >
            {authStore.forgottenPasswordEmailRequested
              ? '              '
              : 'Reset Password'}
          </Button>
        </Form>
        <div className="max-w-md mx-auto bg-secondary p-4 text-center text-white">
          <p>
            Return to{' '}
            <Link
              href={prefix + '/login'}
              className="underline hover:text-white hover:underline"
            >
              Sign in.
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default observer(Forgot);
